import { Box, Divider, Grid, styled } from '@mui/material';
import { CompanyEmploymentTypes, CompanyRole, vatListOptions } from '@types';
import { useFormContext } from 'react-hook-form';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { useGetDepartmentAsDetail } from 'ogp/components/views/management/departments';
import { PolicyGroups } from 'shared/utils/policy-controller';
import { AuthenticatedElement } from 'ogp/components/common/authenticated-element';
import {
  BreakMinutesSelect,
  ClaInput,
  ContactNameInput,
  ContactPhoneWithPrefixInput,
  CostCenterInput,
  DepartmentSelect,
  DescriptionLegalRequirementsInput,
  FlexpoolOnlySwitch,
  HourlyRateInput,
  ImageUrlInput,
  IsEmployeeCheckbox,
  IsFreelanceCheckbox,
  IsFreelanceOrtApplicableSwitch,
  IsTempworkCheckbox,
  IsTempworkOrtApplicableSwitch,
  JobTypeSelect,
  LongDescriptionInput,
  MutableUntilHoursInput,
  ShortDescriptionInput,
  VatSelect,
  WorkNameInput,
  WorkTemplateCertificatesSelect,
  WorkTemplateRequirementsSelect,
} from 'shared/components/forms/inputs';
import { WorkTemplateCreateDataModel } from 'ogp/services/mappers/mappers';
import { useMemo } from 'react';
import { validateAtLeastOneEmploymentType } from 'shared/components/forms/helpers/validators';
import { LocationFormInput } from 'redesign';
import { TimeInputs } from 'shared/components/forms/inputs/time-inputs';
import { MaxClaimantsElement } from 'shared/components/shift-forms/components/forms/shared/inputs/max-claimants-input';
import { FlexPoolsSection } from './flex-pools/flex-pools-section';
import { WorkTemplateFormContext, WorkTemplateFormState } from './work-template-form.types';
import { JobTypeRequiredCertificatesWrapper } from '../../job-type-required-certificates-list/job-type-required-certificates-wrapper';

export const WorkTemplateForm = ({
  employmentType,
  workTemplateCreateData,
  formState,
}: {
  employmentType: CompanyEmploymentTypes;
  workTemplateCreateData: WorkTemplateCreateDataModel;
  formState: WorkTemplateFormState;
}) => {
  const { allowReverseChargingVAT } = useFeatureFlagContext();
  const { watch, setValue, reset, trigger, unregister } = useFormContext<WorkTemplateFormContext>();
  const isTempwork = watch('isTempwork');
  const isFreelance = watch('isFreelance');
  const jobTypeIdWatch = watch('jobType');
  const departmentId = watch('department');
  const { data: departmentData } = useGetDepartmentAsDetail(
    departmentId ? departmentId.toString() : undefined
  );

  const flexpoolDataPrefiltered = useMemo(() => {
    return departmentId
      ? workTemplateCreateData.flexPools.filter((flexpool) =>
          flexpool.departmentIds.includes(departmentId)
        )
      : workTemplateCreateData.flexPools;
  }, [departmentId, workTemplateCreateData.flexPools]);

  const isFreelancePropertyRequired = isFreelance && formState !== 'read';
  const isTempworkPropertyRequired = isTempwork && formState !== 'read';

  return (
    <Grid container spacing={6}>
      <Grid item xs={6}>
        <Box display="flex" flexDirection="row" height="100%">
          <form aria-label="Work template form">
            <Grid container item columnSpacing={4} rowSpacing={1} xs={12}>
              <Grid item xs={12}>
                <AuthenticatedElement
                  roles={CompanyRole.HQADMIN}
                  policyGroup={PolicyGroups.COMPANY}
                >
                  <FlexpoolOnlySwitch disabled={formState === 'read'} />
                </AuthenticatedElement>
              </Grid>
              <Grid item xs={6}>
                <WorkNameInput disabled={formState === 'read'} required={true} />
              </Grid>
              <Grid item xs={6}>
                <DepartmentSelect
                  disabled={formState === 'read' || formState === 'update'}
                  options={workTemplateCreateData.branchOfficesWithDepartments}
                  onChange={() => {
                    if (!departmentData) {
                      return;
                    }
                    setValue('vat', departmentData.VAT);
                    setValue('flexPools', []);
                    reset((formValues: WorkTemplateFormContext) => ({
                      ...formValues,
                      flexPool: '',
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TimeInputs disabled={false} />
              </Grid>
              <Grid item xs={6}>
                <MaxClaimantsElement disabled={false} />
              </Grid>
              <Grid item xs={12}>
                <IsFreelanceCheckbox
                  disabled={!employmentType.offersFreelanceWork || formState === 'read'}
                  onChange={async () => {
                    await Promise.all([
                      trigger('isFreelance'),
                      trigger('isTempwork'),
                      trigger('isEmployee'),
                    ]);
                    unregister('hourlyRate'); // Input needs to be re-registered after checkbox changes so rules are updated
                    unregister('mutableUntilHours'); // Input needs to be re-registered after checkbox changes so rules are updated
                    await Promise.all([trigger('mutableUntilHours'), trigger('hourlyRate')]);
                  }}
                  validate={(val, context) => validateAtLeastOneEmploymentType(context)}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={6}>
                  <IsFreelanceOrtApplicableSwitch
                    disabled={formState === 'read' || !isFreelancePropertyRequired}
                  />
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <HourlyRateInput
                  disabled={!isFreelancePropertyRequired}
                  required={isFreelancePropertyRequired}
                />
              </Grid>
              <Grid item xs={6}>
                <MutableUntilHoursInput
                  disabled={!isFreelancePropertyRequired}
                  required={isFreelancePropertyRequired}
                />
              </Grid>
              <Grid item xs={12}>
                <IsTempworkCheckbox
                  disabled={!employmentType.offersTempWork || formState === 'read'}
                  onChange={async () => {
                    await Promise.all([
                      trigger('isFreelance'),
                      trigger('isTempwork'),
                      trigger('isEmployee'),
                    ]);
                    unregister('cla'); // Input needs to be re-registered after checkbox changes so rules are updated
                    await trigger('cla');
                  }}
                  validate={(val, context) => validateAtLeastOneEmploymentType(context)}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={6}>
                  <IsTempworkOrtApplicableSwitch
                    disabled={formState === 'read' || !isTempworkPropertyRequired}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ClaInput
                  disabled={!isTempworkPropertyRequired}
                  required={isTempworkPropertyRequired}
                />
              </Grid>
              <Grid item xs={12}>
                <IsEmployeeCheckbox
                  disabled={!employmentType.offersEmployeeWork || formState === 'read'}
                  onChange={async () => {
                    await Promise.all([
                      trigger('isFreelance'),
                      trigger('isTempwork'),
                      trigger('isEmployee'),
                    ]);
                  }}
                  validate={(val, context) => validateAtLeastOneEmploymentType(context)}
                />
              </Grid>
              <Grid item xs={6}>
                <BreakMinutesSelect
                  options={workTemplateCreateData.breakOptions}
                  disabled={formState === 'read'}
                  required={true}
                />
              </Grid>
              <Grid item xs={12}>
                <JobTypeSelect
                  disabled={formState === 'read' || formState === 'update'}
                  jobGroups={workTemplateCreateData.jobGroups}
                  required={true}
                />
              </Grid>
              <Grid item xs={12}>
                <WorkTemplateCertificatesSelect
                  disabled={false}
                  options={workTemplateCreateData.jobCertificates}
                />
              </Grid>
              <FlexPoolsSection state={formState} flexPoolOptions={flexpoolDataPrefiltered} />
              <Grid item xs={12}>
                <VatSelect
                  disabled={formState === 'read'}
                  options={vatListOptions(allowReverseChargingVAT)}
                />
              </Grid>
              <Grid item xs={12}>
                <ShortDescriptionInput disabled={formState === 'read'} required={true} />
              </Grid>
              <Grid item xs={12}>
                <LongDescriptionInput disabled={formState === 'read'} required={true} />
              </Grid>
              <WorkTemplateRequirementsSelect
                disabled={formState === 'read'}
                options={workTemplateCreateData.jobRequirements}
              />
              <Grid item xs={12}>
                <DescriptionLegalRequirementsInput
                  disabled={formState === 'read'}
                  required={true}
                />
              </Grid>
              <Grid item xs={6}>
                <ContactNameInput disabled={formState === 'read'} required={true} />
              </Grid>
              <Grid item xs={6}>
                <ContactPhoneWithPrefixInput disabled={formState === 'read'} required={true} />
              </Grid>
              <Grid item xs={12}>
                <CostCenterInput
                  disabled={formState === 'read'}
                  helperText={
                    departmentData?.costCenter
                      ? `De standaard voor deze afdeling is ${departmentData.costCenter}`
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <LocationFormInput disabled={formState === 'read'} />
              </Grid>
              <AuthenticatedElement roles={CompanyRole.HQADMIN} policyGroup={PolicyGroups.COMPANY}>
                <Grid item xs={12}>
                  <ImageUrlInput disabled={formState === 'read'} />
                </Grid>
              </AuthenticatedElement>
            </Grid>
          </form>
          <Box>
            <StyledDivider orientation="vertical" />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <JobTypeRequiredCertificatesWrapper jobTypeId={jobTypeIdWatch} />
      </Grid>
    </Grid>
  );
};

const StyledDivider = styled(Divider)(({ theme }) => ({
  height: '100%',
  marginLeft: theme.spacing(7),
  width: 2,
}));
